/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { MED_DATETIME_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography, Typography2 } from '@coursera/cds-core';

import { isVivaExamEnabled } from 'bundles/academic-integrity/lib/epics';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import AttemptsLink from 'bundles/grading/components/staff-graded-assignments/attempts/AttemptsLink';
import PlagiarismFlag from 'bundles/grading/components/staff-graded-assignments/plagiarism/PlagiarismFlag';
import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';

import _t from 'i18n!nls/grading';

const styles = {
  container: () => css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: var(--cds-spacing-300) 0 0 0;
  `,
  submissionDate: () => css`
    margin-top: var(--cds-spacing-50);
  `,
  leftContainer: () => css`
    display: flex;
    flex-direction: column;
  `,
  rightContainer: () => css`
    margin-left: var(--cds-spacing-50);
  `,
  attemptsLinkContainer: () => css`
    /* The attempts link has padding around the text, shifting it to the right so it's aligned with the submission time. */
    margin-right: calc(var(--cds-spacing-100) * -1);
  `,
};

type Props = {
  submitter?: SGASubmitter;
};

const SubmissionHeading = ({ submitter }: Props) => {
  const {
    pageData: { courseId, currentItemId },
    isPrivateSession,
  } = useContext(SGAPageContext);

  const vivaExamEnabled = isVivaExamEnabled(courseId, currentItemId);

  const submissionDate = submitter?.submittedAt?.seconds ? parseInt(submitter.submittedAt.seconds, 10) * 1000 : null;
  return (
    <div css={styles.container}>
      <div css={styles.leftContainer}>
        <Typography className="pii-hide" variant="h2semibold">
          {submitter?.name}
        </Typography>
        {submissionDate && (
          <Typography2 component="p" variant="bodySecondary" css={styles.submissionDate}>
            {_t('Submitted:')} {formatDateTimeDisplay(submissionDate, MED_DATETIME_DISPLAY)}
          </Typography2>
        )}
        {isPrivateSession && !vivaExamEnabled && <PlagiarismFlag />}
      </div>
      <div css={styles.rightContainer}>
        <div css={styles.attemptsLinkContainer}>
          <AttemptsLink />
        </div>
      </div>
    </div>
  );
};

export default SubmissionHeading;
